import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@/app/components/form/Input';
import type { OptionType } from '@/app/components/form/MultiSearchInput/types';
import { PhoneInput } from '@/app/components/form/PhoneInput';
import type { PhoneInputValue } from '@/app/components/form/PhoneInput/types';
import { InfinityAsyncSelector } from '@/app/components/form/Selector/InfinityAsyncSelector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { RelationshipData } from '@/types/clientDetailsApiTypes';
import type { CreateClientFormData } from '@/types/clientsApiTypes';

const fieldPrefix = 'primary_passenger.client';

function toEmergencyRelationship(
  option: SelectorOption<string | number> | null
): OptionType<number, string> | null {
  if (!option) return null;
  const value = option.value;
  return {
    value: value != null ? Number(value) : null,
    label: option.label ?? '',
  };
}

export const EditEmergencyContactForm = () => {
  const {
    control,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  const phone = watch(`${fieldPrefix}.emergency_contact_phone`);
  const handlePhoneChange = (value?: PhoneInputValue) => {
    setValue(`${fieldPrefix}.emergency_contact_phone`, value?.phoneWithMask ?? '', { shouldValidate: true });
  };

  const handleRelationshipChange = (option: SelectorOption<string | number> | null) => {
    const relationship = toEmergencyRelationship(option);
    const relationshipTypeId = relationship?.value ?? null;
    setValue(`${fieldPrefix}.emergency_relationship_type_id`, relationshipTypeId, { shouldValidate: true });
    setValue(`${fieldPrefix}.emergency_relationship`, relationship, { shouldValidate: true });
  };

  const onMapOptions = (options: RelationshipData[]) =>
    options.map((option) => ({
      value: option.id,
      label: option.name,
    }));

  return (
    <div className="grid grid-cols-1 gap-4">
      <Input
        {...register(`${fieldPrefix}.emergency_contact_name`)}
        placeholder="Enter name"
        label="Name"
        className="w-full"
        errors={errors}
        intent="primaryWhite"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          {...register(`${fieldPrefix}.emergency_contact_email`)}
          placeholder="Enter email address"
          label="Email"
          className="w-full"
          errors={errors}
          intent="primaryWhite"
        />

        <PhoneInput
          name={`${fieldPrefix}.emergency_contact_phone`}
          value={phone || ''}
          onChange={handlePhoneChange}
          setError={setError}
          clearErrors={clearErrors}
          label="Phone number"
          valueType="phoneWithMask"
          placeholder="Enter phone number"
          errors={errors}
        />
      </div>

      <Controller
        name={`${fieldPrefix}.emergency_relationship`}
        control={control}
        render={({ field }) => (
          <InfinityAsyncSelector<RelationshipData>
            name={`${fieldPrefix}.emergency_relationship`}
            url="/api/relationship-types"
            option={field.value as SelectorOption<string | number> | null}
            value={field.value?.value ?? ''}
            mapOptions={onMapOptions}
            onChange={handleRelationshipChange}
            queryKey={['relationship-type']}
            label="Relationship"
            placeholder="Select relationship"
            menuPosition="fixed"
            errors={errors}
          />
        )}
      />
    </div>
  );
};